import { c as _c } from "react/compiler-runtime";
import { parseResourcePattern } from '../../../../../../../../domain/issue-trigger-patterns';
import { APIDestination } from '../../../../../../../../typings/Destination.interface';
import { IssueType } from '../../../../../../../../typings/Issue.interface';
import { APIIssueTrigger, IssueTriggerConfigs, IssueTriggerDeliveryConfigs, IssueTriggerRequestConfigs, IssueTriggerTransformationConfigs } from '../../../../../../../../typings/IssueTrigger.interface';
import { APISource } from '../../../../../../../../typings/Source.interface';
import { Transformation } from '../../../../../../../../typings/Transformation.interface';
import { APIWebhook } from '../../../../../../../../typings/Webhook.interface';
import { capitalizeFirstLetter } from '../../../../../utils';
import { getMsIntervalByUnit } from '../../../../../utils/rules';
import Chip from '../../../../common/base/Chip';
import Divider from '../../../../common/base/Divider';
import Icon from '../../../../common/base/Icon';
import Text from '../../../../common/base/Text';
import Tooltip from '../../../../common/base/Tooltip';
import { Div } from '../../../../common/helpers/StyledUtils';
import ClickableRow from '../../../../common/Table/ClickableRow';
import { IssueTriggersRenderData } from './';
type GetReferenceName = (reference: APIWebhook | Transformation | APIDestination | APISource) => string;
const referenceToText = (link_prefix: string, reference: '*' | string | string[], reference_data: Record<string, APIWebhook | Transformation | APIDestination | APISource>, label: string, getReferenceName: GetReferenceName): {
  pre: string;
  values: string | {
    label: string;
    to: string;
  }[];
} => {
  if (reference === '*') {
    return {
      pre: 'for',
      values: `All ${label}`
    };
  }
  if (Array.isArray(reference)) {
    return {
      pre: `for ${label.toLocaleLowerCase()}`,
      values: reference.filter(ref => !!reference_data[ref]).map(ref => ({
        label: getReferenceName(reference_data[ref]),
        to: `${link_prefix}/${ref}`
      }))
    };
  }
  const {
    is_negated,
    pattern
  } = parseResourcePattern(reference);
  if (is_negated) {
    return {
      pre: `for all ${label.toLocaleLowerCase()} except those matching`,
      values: pattern
    };
  }
  return {
    pre: `for ${label.toLocaleLowerCase()} with pattern`,
    values: reference
  };
};
const content_configs: Record<IssueType, {
  texts: string[];
  configsToText: Record<keyof IssueTriggerConfigs, (config: IssueTriggerConfigs) => string>;
  render_data_key: keyof IssueTriggersRenderData;
  reference_label: string;
  reference_config_key: string;
  getReferenceName: GetReferenceName;
}> = {
  delivery: {
    texts: ['Open issue after the', '$strategy', 'delivery attempt failure'],
    configsToText: {
      strategy: configs => capitalizeFirstLetter((configs as IssueTriggerDeliveryConfigs).strategy.split('_')[0])
    },
    reference_config_key: 'connections',
    render_data_key: 'webhooks_by_id',
    reference_label: 'Connections',
    getReferenceName: (ref: APIWebhook) => ref.full_name
  },
  transformation: {
    texts: ['Open issue on execution with log level', '$log_level', 'or higher'],
    configsToText: {
      log_level: configs => capitalizeFirstLetter((configs as IssueTriggerTransformationConfigs).log_level)
    },
    reference_config_key: 'transformations',
    render_data_key: 'transformations_by_id',
    reference_label: 'Transformations',
    getReferenceName: (ref: Transformation) => ref.name
  },
  backpressure: {
    texts: ['Open issue when backpressure is', '$delay', 'or higher'],
    configsToText: {
      delay: configs => {
        const [value, interval] = getMsIntervalByUnit(configs.delay, 'delay');
        return `${value} ${interval}`;
      }
    },
    reference_config_key: 'destinations',
    render_data_key: 'destinations_by_id',
    reference_label: 'Destinations',
    getReferenceName: (ref: Transformation) => ref.name
  },
  request: {
    texts: ['Open issue when request is rejected with cause', '$rejection_causes'],
    configsToText: {
      rejection_causes: configs => {
        const requestConfigs = configs as IssueTriggerRequestConfigs;
        const causes = requestConfigs.rejection_causes;
        const excludedCauses = requestConfigs.excluded_rejection_causes;

        // If exclusions are set (rejection_causes must be '*' when exclusions are used)
        if (excludedCauses && Array.isArray(excludedCauses) && excludedCauses.length > 0) {
          const formattedExclusions = excludedCauses.map(cause => cause.toLowerCase().replace(/_/g, ' ')).join(', ');
          return `all except ${formattedExclusions}`;
        }
        if (causes === '*') return 'any';
        return Array.isArray(causes) ? causes.join(', ').toLowerCase().replace(/_/g, ' ') : causes;
      }
    },
    reference_config_key: 'sources',
    render_data_key: 'sources_by_id',
    reference_label: 'Sources',
    getReferenceName: (ref: APISource) => ref.name
  }
};
const trigger_type_to_link_prefix: Record<IssueType, string> = {
  delivery: '/connections',
  transformation: '/transformations',
  backpressure: '/destinations',
  request: '/sources'
};
const IssueTriggerCard: React.FC<{
  issue_trigger: APIIssueTrigger;
  render_data: IssueTriggersRenderData;
}> = t0 => {
  const $ = _c(49);
  const {
    issue_trigger,
    render_data
  } = t0;
  const {
    texts,
    configsToText,
    render_data_key,
    reference_label,
    reference_config_key,
    getReferenceName
  } = content_configs[issue_trigger.type];
  let T0;
  let T1;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  if ($[0] !== configsToText || $[1] !== getReferenceName || $[2] !== issue_trigger.configs || $[3] !== issue_trigger.id || $[4] !== issue_trigger.type || $[5] !== reference_config_key || $[6] !== reference_label || $[7] !== render_data || $[8] !== render_data_key || $[9] !== texts) {
    const {
      pre,
      values
    } = referenceToText(trigger_type_to_link_prefix[issue_trigger.type], issue_trigger.configs[reference_config_key], render_data[render_data_key], reference_label, getReferenceName);
    T1 = ClickableRow;
    if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = {
        x: 6,
        y: 4
      };
      $[17] = t4;
    } else {
      t4 = $[17];
    }
    t5 = `/issue-triggers/${issue_trigger.id}`;
    T0 = Div;
    if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = {
        wrap: true,
        gap: 2
      };
      $[18] = t1;
    } else {
      t1 = $[18];
    }
    let t6;
    if ($[19] !== configsToText || $[20] !== issue_trigger.configs || $[21] !== texts) {
      let t7;
      if ($[23] !== configsToText || $[24] !== issue_trigger.configs) {
        t7 = (text, i) => {
          if (text.indexOf("$") === 0) {
            const key = text.split("$")[1];
            return configsToText[key] && <Text key={key} bold={true} dark={true} as="span">{i !== 0 ? " " : ""}{configsToText[key](issue_trigger.configs).toLowerCase()}</Text>;
          }
          return `${i !== 0 ? " " : ""}${text}`;
        };
        $[23] = configsToText;
        $[24] = issue_trigger.configs;
        $[25] = t7;
      } else {
        t7 = $[25];
      }
      t6 = texts.map(t7);
      $[19] = configsToText;
      $[20] = issue_trigger.configs;
      $[21] = texts;
      $[22] = t6;
    } else {
      t6 = $[22];
    }
    t2 = <Text as="span">{t6}{" "}{pre}{" "}{typeof values === "string" && <Text bold={true} dark={true} as="span">{values?.toLowerCase()}</Text>}</Text>;
    t3 = Array.isArray(values) && <>{values.filter(_temp).map(_temp2)}{values.length > 3 && <ClickableRow.Content><Tooltip max_width={516} tooltip={<Div max_w={{
          px: 492
        }} flex={{
          gap: 2,
          wrap: true
        }}>{values.filter(_temp3).map(_temp4)}</Div>}><Text semi={true} muted={true} as="span">+ {values.length - 3} more</Text></Tooltip></ClickableRow.Content>}</>;
    $[0] = configsToText;
    $[1] = getReferenceName;
    $[2] = issue_trigger.configs;
    $[3] = issue_trigger.id;
    $[4] = issue_trigger.type;
    $[5] = reference_config_key;
    $[6] = reference_label;
    $[7] = render_data;
    $[8] = render_data_key;
    $[9] = texts;
    $[10] = T0;
    $[11] = T1;
    $[12] = t1;
    $[13] = t2;
    $[14] = t3;
    $[15] = t4;
    $[16] = t5;
  } else {
    T0 = $[10];
    T1 = $[11];
    t1 = $[12];
    t2 = $[13];
    t3 = $[14];
    t4 = $[15];
    t5 = $[16];
  }
  let t6;
  if ($[26] !== T0 || $[27] !== t1 || $[28] !== t2 || $[29] !== t3) {
    t6 = <T0 flex={t1}>{t2}{t3}</T0>;
    $[26] = T0;
    $[27] = t1;
    $[28] = t2;
    $[29] = t3;
    $[30] = t6;
  } else {
    t6 = $[30];
  }
  let t7;
  let t8;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      t: 1
    };
    t8 = {
      align: "center",
      gap: 1
    };
    $[31] = t7;
    $[32] = t8;
  } else {
    t7 = $[31];
    t8 = $[32];
  }
  const t9 = issue_trigger.disabled_at ? "notification_off" : "notification";
  let t10;
  if ($[33] !== t9) {
    t10 = <Icon muted={true} icon={t9} />;
    $[33] = t9;
    $[34] = t10;
  } else {
    t10 = $[34];
  }
  const t11 = issue_trigger.disabled_at ? "Disabled" : "Enabled";
  let t12;
  if ($[35] !== t11) {
    t12 = <Text size="xs" semi={true} muted={true}>{t11}</Text>;
    $[35] = t11;
    $[36] = t12;
  } else {
    t12 = $[36];
  }
  let t13;
  if ($[37] !== t10 || $[38] !== t12) {
    t13 = <Div m={t7} flex={t8}>{t10}{t12}</Div>;
    $[37] = t10;
    $[38] = t12;
    $[39] = t13;
  } else {
    t13 = $[39];
  }
  let t14;
  if ($[40] !== T1 || $[41] !== t13 || $[42] !== t4 || $[43] !== t5 || $[44] !== t6) {
    t14 = <T1 p={t4} to={t5}>{t6}{t13}</T1>;
    $[40] = T1;
    $[41] = t13;
    $[42] = t4;
    $[43] = t5;
    $[44] = t6;
    $[45] = t14;
  } else {
    t14 = $[45];
  }
  let t15;
  if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <Divider />;
    $[46] = t15;
  } else {
    t15 = $[46];
  }
  let t16;
  if ($[47] !== t14) {
    t16 = <>{t14}{t15}</>;
    $[47] = t14;
    $[48] = t16;
  } else {
    t16 = $[48];
  }
  return t16;
};
export default IssueTriggerCard;
function _temp(_, i_0) {
  return i_0 < 3;
}
function _temp2(t0) {
  const {
    label,
    to
  } = t0;
  return <ClickableRow.Content key={to}><Chip label={label} to={to} mono={true} outline={true} /></ClickableRow.Content>;
}
function _temp3(__0, i_1) {
  return i_1 >= 3;
}
function _temp4(t0) {
  const {
    label: label_0,
    to: to_0
  } = t0;
  return <Chip label={label_0} to={to_0} mono={true} key={to_0} outline={true} />;
}